import { IconMenu2, type Icon } from "@tabler/icons-react";
import { motion } from "motion/react";
import { useLayoutEffect, useRef, useState } from "react";
import { Link, matchPath, useLocation, useNavigation } from "react-router-dom";
import { classNames } from "src/core/utilities/classNames";
import { OpeningSpinner } from "../OpeningSpinner";
import { pillSpring } from "./frameMotion";
import { useMainMenuItems } from "./useMainMenuItems";

interface Props {
    moreOpen: boolean;
    onMore: () => void;
}

const INDICATOR_WIDTH = 56;

const itemClass = "group relative flex flex-col items-center gap-1 w-16 pt-1.5 pb-1";

interface ItemContentProps {
    icon: Icon;
    label: string;
    active: boolean;
    opening?: boolean;
}

const ItemContent = ({ icon: IconEl, label, active, opening = false }: ItemContentProps) => (
    <>
        <span className="relative grid place-items-center h-8 w-14 rounded-full">
            {!active && <span className="absolute inset-0 rounded-full transition-colors group-hover:bg-base-content/10" />}

            {opening ? (
                <OpeningSpinner className="w-5 relative text-on-indicator" />
            ) : (
                <IconEl className={classNames("w-6 h-6 relative", active ? "text-on-indicator" : "text-base-content/70")} />
            )}
        </span>

        <span className={classNames("text-xs leading-tight whitespace-nowrap", active ? "font-extrabold" : "font-semibold text-base-content/70")}>
            {label}
        </span>
    </>
);

/**
 * The phone's bar, floating above the page. It stays put while the page scrolls. More
 * holds the indicator while the sheet is open, and while the page is one only the sheet
 * leads to. A screen being opened takes the indicator at once, before its code arrives.
 */
export const FloatingNavBar = ({ moreOpen, onMore }: Props) => {
    const location = useLocation();
    const navigation = useNavigation();
    const opening = navigation.state === "loading";
    const pathname = navigation.location?.pathname ?? location.pathname;

    const items = useMainMenuItems().filter((item) => item.bottomNav);
    const current = items.find((item) => matchPath({ path: item.to, end: item.bottomEnd ?? false }, pathname));
    const moreActive = moreOpen || !current;
    const activeIndex = moreActive || !current ? items.length : items.indexOf(current);

    const itemRefs = useRef<(HTMLElement | null)[]>([]);
    const [indicatorX, setIndicatorX] = useState<number | null>(null);

    /**
     * One indicator for the bar, moved along x only. A shared-layout pill measures against
     * the page, so scroll restoration after navigating showed as the pill jumping up and down.
     */
    useLayoutEffect(() => {
        const item = itemRefs.current[activeIndex];

        if (item) setIndicatorX(item.offsetLeft + (item.offsetWidth - INDICATOR_WIDTH) / 2);
    }, [activeIndex, items.length]);

    return (
        <nav
            data-floating-nav
            aria-label="Main"
            className="lg:hidden fixed inset-x-0 bottom-[calc(1.25rem+env(safe-area-inset-bottom))] z-50 flex justify-center pointer-events-none"
        >
            <div className="pointer-events-auto relative flex gap-1 px-2 py-1.5 rounded-[2rem] bg-frame-high shadow-[0_1px_3px_rgb(0_0_0/0.12),0_6px_16px_rgb(24_32_58/0.16)]">
                {indicatorX !== null && (
                    <motion.span
                        aria-hidden="true"
                        className="absolute top-3 left-0 h-8 rounded-full bg-indicator"
                        style={{ width: INDICATOR_WIDTH }}
                        initial={false}
                        animate={{ x: indicatorX }}
                        transition={pillSpring}
                    />
                )}

                {items.map((item, index) => {
                    const active = index === activeIndex;

                    return (
                        <Link
                            key={item.to}
                            ref={(element) => (itemRefs.current[index] = element)}
                            to={item.to}
                            aria-current={active ? "page" : undefined}
                            className={itemClass}
                        >
                            <ItemContent
                                icon={item.icon}
                                label={item.shortLabel ?? item.label}
                                active={active}
                                opening={active && opening}
                            />
                        </Link>
                    );
                })}

                <button
                    type="button"
                    ref={(element) => (itemRefs.current[items.length] = element)}
                    aria-expanded={moreOpen}
                    onClick={onMore}
                    className={itemClass}
                >
                    <ItemContent icon={IconMenu2} label="More" active={moreActive} opening={!moreOpen && !current && opening} />
                </button>
            </div>
        </nav>
    );
};
